import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Business,
  Star,
  Directions,
  People,
  SportsTennis,
} from '@mui/icons-material';
import apiService from '../services/api';
import { Club, Court } from '../types';

export const ClubsPage: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClubs();
    fetchCourts();
  }, []);

  const fetchClubs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getClubs();
      setClubs(response.data || []);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      setError('Failed to load clubs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourts = async () => {
    try {
      const response = await apiService.getAllCourts();
      setCourts(response.data || []);
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const filteredClubs = clubs.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderClubCard = (club: Club) => (
    <Card key={club.id} sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {club.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Chip
                label={club.is_active ? 'Active' : 'Inactive'}
                color={club.is_active ? 'success' : 'default'}
          size="small"
        />
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Get Directions">
              <IconButton size="small">
                <Directions />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Details">
              <IconButton size="small">
                <Business />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <LocationOn fontSize="small" />
          <Typography variant="body2">
            {club.address}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <SportsTennis fontSize="small" />
            <Typography variant="body2">
              Available courts
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <People fontSize="small" />
            <Typography variant="body2">
              Active club
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Clubs & Courts
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover padel clubs and courts in your area
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search clubs by name or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Stats Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{clubs.length}</Typography>
                  <Typography color="textSecondary">Total Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SportsTennis color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.length}
                  </Typography>
                  <Typography color="textSecondary">Total Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <People color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.filter(club => club.is_active).length}
                  </Typography>
                  <Typography color="textSecondary">Active Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Star color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {clubs.filter(club => club.is_active).length}
                  </Typography>
                  <Typography color="textSecondary">Active Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Clubs List */}
      {filteredClubs.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Business sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No clubs found
          </Typography>
          <Typography color="text.secondary" paragraph>
            Try adjusting your search criteria or check back later for new clubs.
          </Typography>
        </Box>
      ) : (
        filteredClubs.map(renderClubCard)
      )}

      {/* Courts Overview */}
      <Card sx={{ mt: 3 }}>
        <CardHeader title="All Courts" />
        <CardContent>
          {courts.length === 0 ? (
            <Box textAlign="center" py={2}>
              <SportsTennis sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No courts available
              </Typography>
            </Box>
          ) : (
            <List>
              {courts.slice(0, 10).map((court, index) => (
                <React.Fragment key={court.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <SportsTennis />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={court.name}
                      secondary={`${(() => {
                        const club = clubs.find(club => club.id === court.club_id);
                        return club?.name || `Unknown Club (ID: ${court.club_id})`;
                      })()}`}
                    />
                    <Chip
                      label={court.is_active ? 'Active' : 'Inactive'}
                      color={court.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </ListItem>
                  {index < Math.min(courts.length, 10) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}; 
